import { useCallback, useEffect, useState } from "react";
import { Handshake, Plus, Loader2, Search, Mail, Building2, ShieldCheck, ShieldOff, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PartnerInviteDialog } from "../components/partners/PartnerInviteDialog";
import { PartnerDetail } from "../components/partners/PartnerDetail";
import { STATUS_LABEL, STATUS_CLS } from "../components/partners/constants";
import { PartnerDashboard } from "../components/partners/PartnerDashboard";

export default function PartnersAdminPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [partners, setPartners] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [societeFilter, setSocieteFilter] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, r] = await Promise.all([api.get("/partners"), api.get("/societes"), api.get("/builder/registry")]);
      setPartners(p.data || []);
      const socs = Array.isArray(s.data) ? s.data : (s.data?.items || []);
      setSocietes(socs);
      setRegistry(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);

  const typeLabel = (k) => registry?.partner_types?.find((t) => t.key === k)?.label || k;
  const filtered = partners.filter((p) => (!societeFilter || p.societe_id === societeFilter) && (!typeFilter || p.type === typeFilter) && (!search || `${p.name} ${p.company} ${p.email}`.toLowerCase().includes(search.toLowerCase())));

  const onSaved = (p) => {
    setPartners((ps) => ps.some((x) => x.partner_id === p.partner_id) ? ps.map((x) => (x.partner_id === p.partner_id ? { ...x, ...p } : x)) : [p, ...ps]);
    if (selected?.partner_id === p.partner_id) setSelected((s) => ({ ...s, ...p }));
  };
  const onDeleted = (id) => { setPartners((ps) => ps.filter((x) => x.partner_id !== id)); setSelected(null); };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="partners-admin-page">
      <PageHeader
        icon={<Handshake />} title="Partenaires" subtitle="Fournisseurs, formateurs externes, sous-traitants et apporteurs d'affaires — accès configurables par société."
        actions={<Button onClick={() => setShowInvite(true)} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="partner-invite-btn"><Plus className="w-4 h-4 mr-2" /> Inviter un partenaire</Button>}
      />

      <div className="mb-5">
        <PartnerDashboard societeId={societeFilter || null} onOpenPartner={(pid) => { const p = partners.find((x) => x.partner_id === pid); if (p) setSelected(p); }} />
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-4">
        <select value={societeFilter} onChange={(e) => setSocieteFilter(e.target.value)} className="bg-[#0b0811] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="partners-societe-filter">
          <option value="">Toutes les sociétés</option>
          {societes.map((s) => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
        </select>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 bg-[#0b0811] border-white/10 text-white w-64" data-testid="partners-search" />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-[#0b0811] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="partners-type-filter">
          <option value="">Tous les types</option>
          {(registry?.partner_types || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <div className="text-xs text-slate-500">{filtered.length} partenaire{filtered.length > 1 ? "s" : ""}</div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className={"col-span-12 " + (selected ? "lg:col-span-5" : "")}>
          {loading ? <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>
          : filtered.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-white/10 rounded-xl bg-[#0b0811]">
              <Handshake className="w-10 h-10 mx-auto text-slate-700 mb-3" />
              <p className="text-sm text-slate-500">Aucun partenaire — invitez un fournisseur, un formateur externe, un sous-traitant ou un apporteur d'affaires.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="partners-list">
              {filtered.map((p) => (
                <button key={p.partner_id} onClick={() => setSelected(p)} data-testid={"partner-row-" + p.partner_id}
                  className={"w-full text-left bg-[#14101c] border rounded-xl px-4 py-3 flex items-center gap-3 hover:border-[#f7c935]/30 transition-colors " + (selected?.partner_id === p.partner_id ? "border-[#f7c935]/50" : "border-white/5")}>
                  <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-sm font-black text-[#f7c935]">{(p.company || p.name || "?").slice(0, 1).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white text-sm truncate">{p.company || p.name}</span>
                      <span className={"text-[9px] px-1.5 py-0.5 rounded uppercase font-bold " + (STATUS_CLS[p.status] || "")} data-testid={"partner-status-" + p.partner_id}>{STATUS_LABEL[p.status] || p.status}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                      <span className="text-sky-300 font-semibold">{typeLabel(p.type)}</span>
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{p.email}</span>
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{p.societe_name}</span>
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1">
                    {Object.values(p.access || {}).filter(Boolean).length > 0 ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> : <ShieldOff className="w-3.5 h-3.5 text-red-400" />}
                    {Object.values(p.access || {}).filter(Boolean).length} mod.
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </button>
              ))}
            </div>
          )}
        </div>
        {selected && (
          <div className="col-span-12 lg:col-span-7">
            <PartnerDetail key={selected.partner_id} partner={selected} api={api} registry={registry} onClose={() => setSelected(null)} onSaved={onSaved} onDeleted={onDeleted} />
          </div>
        )}
      </div>

      {showInvite && <PartnerInviteDialog api={api} societes={societes} registry={registry} onClose={() => setShowInvite(false)} onInvited={(p) => { onSaved(p); setSelected(p); }} />}
    </div>
  );
}
